import { areaToTitle } from './areaToTitle';
import { convertToUrlParams } from './convertSearchParams';
import { datesToTitle } from './datesToTitle';
import { getCategoryName } from './getCategoryName';
import { relativePath } from './redirectToRelativePath';

type BreadCrumbsContent = {
  links: {
    label: string;
    href: string;
  }[];
  current: string;
};

export const getBreadCrumbsContent = (
  customerSetting: CustomerSetting,
  currentTitle: string,
  searchParams?: SearchParams,
  today?: boolean,
  subPages?: BreadCrumbsContent['links']
): BreadCrumbsContent => {
  if (currentTitle === '') {
    // 検索条件に設定したものを抽出
    const steps: { key: string; label: string }[] = [];
    if (searchParams?.category) {
      const categoryName = getCategoryName(
        Number(searchParams.category),
        customerSetting
      );
      steps.push({
        key: 'category',
        label: categoryName + 'カテゴリ'
      });
    }
    if (searchParams?.area) {
      const areaName = areaToTitle(
        Number(searchParams.area),
        customerSetting.areas
      );
      if (areaName) {
        steps.push({
          key: 'area',
          label: areaName
        });
      }
    }
    if (today) {
      steps.push({
        key: 'today',
        label: '今日'
      });
    } else if (searchParams?.dates) {
      const dates = datesToTitle(searchParams.dates);
      if (dates) {
        steps.push({
          key: 'dates',
          label: dates
        });
      }
    }
    if (searchParams?.keyword) {
      steps.push({
        key: 'keyword',
        label: `「${searchParams.keyword}」を含む`
      });
    }

    const links: BreadCrumbsContent['links'] = [
      {
        label: customerSetting.siteName,
        href: relativePath('/', customerSetting.domain)
      },
      ...(subPages || [])
    ];

    steps.forEach((step, index) => {
      // indexとそれより前の条件を取得
      const prevSearchParams = { ...searchParams };
      const nextSteps = steps.slice(index + 1);
      nextSteps.forEach((nextStep) => {
        delete prevSearchParams[nextStep.key as keyof SearchParams];
      });
      links.push({
        label: step.label,
        href: relativePath(
          `/list/?` +
            convertToUrlParams(prevSearchParams).toString() +
            '#events',
          customerSetting.domain
        )
      });
    });

    return {
      links: links.slice(0, -1),
      current: links.slice(-1)[0].label
    };
  } else {
    return {
      links: [
        {
          label: customerSetting.siteName,
          href: relativePath('/', customerSetting.domain)
        },
        ...(subPages || [])
      ],
      current: currentTitle
    };
  }
};

export const getSearchParamTitle = (
  customerSetting: CustomerSetting,
  searchParams?: SearchParams,
  today?: boolean
) => {
  const { links, current } = getBreadCrumbsContent(
    customerSetting,
    '',
    searchParams,
    today
  );
  const queryLinks = links.filter(
    (link) => link.href !== relativePath('/', customerSetting.domain)
  );
  let title =
    customerSetting.customerId === 38
      ? makeTitleDecoratorForNara(customerSetting, queryLinks, current)
      : makeTitleDecorator(customerSetting, queryLinks, current);
  // 新着順の場合は「新着順の」をつける
  if (searchParams?.sort === 'new') {
    title = `${title}新着順の`;
  }
  return `${title}イベント`;
};

const makeTitleDecorator = (
  customerSetting: CustomerSetting,
  queryLinks: BreadCrumbsContent['links'],
  current: string
) => {
  let text = customerSetting.municipalityName;
  queryLinks.forEach((link) => {
    text += `の${link.label}`;
  });
  if (current === customerSetting.siteName) {
    return text + 'の';
  }
  if (current.endsWith('を含む')) {
    return text + `の${current}`;
  }
  if (current) {
    return text + `の${current}の`;
  }
  return text + 'の';
};

const makeTitleDecoratorForNara = (
  customerSetting: CustomerSetting,
  queryLinks: BreadCrumbsContent['links'],
  current: string
) => {
  const conditions: string[] = [];
  queryLinks.forEach((link) => {
    conditions.push(link.label);
  });
  if (current === customerSetting.siteName) {
    return `${conditions.join('の')}${conditions.length > 0 ? 'の' : ''}`;
  }
  if (current.endsWith('を含む')) {
    conditions.push(current);
    return conditions.join('の');
  }
  if (current) {
    conditions.push(current);
    return `${conditions.join('の')}の`;
  }
  return `${conditions.join('の')}${conditions.length > 0 ? 'の' : ''}`;
};
